import { FunctionComponent, useMemo, useRef, useState } from 'react'
import { MapShapeProps } from './Shape'
import L from 'leaflet'
import { Marker } from 'react-leaflet'

const getAuthorizedValue = (value: number, min: number, max: number) => {
  if (value < min) {
    return min
  }

  return Math.min(value, max)
}

function generateSvg({
  tableNumber,
  numberOfSeats,
  svgScaleFactor,
  rotation = 0,
  hover = false,
}: {
  tableNumber: string
  numberOfSeats: number
  svgScaleFactor: number
  rotation?: number
  hover?: boolean
}) {
  //Define hover effect
  const fillColor = hover ? 'url(#gradHover)' : 'url(#grad1)'

  // Calculate the radius of the table circle based on the number of seats
  const baseTableCircleRadius = 50 * svgScaleFactor // Minimum size of the table circle
  const tableCircleSizePerSeat = 3 * svgScaleFactor // Additional size per seat
  const mainCircleRadius =
    baseTableCircleRadius + tableCircleSizePerSeat * numberOfSeats

  // Constants for seat circle size and spacing
  const seatCircleRadius = 25 * svgScaleFactor
  const spacing = -15 // Space between table and seat circles

  // Calculate the distance from the center of the table to the center of the seat circles
  const distanceFromCenter = mainCircleRadius + seatCircleRadius + spacing

  // Define viewBox size to fit the largest possible table with seats
  const viewBoxSize = (distanceFromCenter + seatCircleRadius) * 2
  const svgWidth = viewBoxSize
  const svgHeight = viewBoxSize
  const centerX = svgWidth / 2
  const centerY = svgHeight / 2

  let svgString = `
        <svg width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${viewBoxSize} ${viewBoxSize}" xmlns="http://www.w3.org/2000/svg">
        <defs>
        <linearGradient id="grad1" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" style="stop-color:rgb(255,255,255);stop-opacity:1" />
        <stop offset="100%" style="stop-color:rgb(245,245,245);stop-opacity:1" />
        </linearGradient>
        <linearGradient id="gradHover" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" style="stop-color:#F3F3F3;stop-opacity:1" /> <!-- Lighter or different color for hover -->
        <stop offset="100%" style="stop-color:#E5E5E5;stop-opacity:1" />
        </linearGradient>
        <filter id="f1" x="0" y="0" width="200%" height="200%">
        <feOffset result="offOut" in="SourceGraphic" dx="0" dy="2" />
        <feGaussianBlur result="blurOut" in="offOut" stdDeviation="2" />
        <feBlend in="SourceGraphic" in2="blurOut" mode="normal" />
        </filter>
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur in="SourceAlpha" stdDeviation="2" result="blur" /> <!-- Blur effect -->
        <feOffset in="blur" dx="0" dy="2" result="offsetBlur" /> <!-- Shadow offset -->
        <feFlood flood-color="rgba(0,0,0,0.5)" result="color"/> <!-- Change 'red' to your desired shadow color -->
        <feComposite in="color" in2="offsetBlur" operator="in" result="shadow" />
        <feComponentTransfer in="shadow" result="transparency">
        <feFuncA type="linear" slope="0.3"/> <!-- Adjust transparency -->
        </feComponentTransfer>
        <feMerge> 
        <feMergeNode in="transparency"/>
        <feMergeNode in="SourceGraphic"/>
        </feMerge>
        </filter>
        </defs>
        <g transform="translate(${centerX}, ${centerY}) rotate(${rotation} 0 0)">`

  // Generate seat circles around the table
  for (let i = 0; i < numberOfSeats; i++) {
    const angle = (i / numberOfSeats) * (2 * Math.PI) // Angle in radians
    const seatX = distanceFromCenter * Math.cos(angle)
    const seatY = distanceFromCenter * Math.sin(angle)

    svgString += `
            <circle cx="${seatX}" cy="${seatY}" r="${seatCircleRadius}" fill="${fillColor}" stroke="#00000033" stroke-width="1" filter="url(#f1)"/>`
  }

  // Central table circle
  svgString += `
            <circle cx="0" cy="0" r="${mainCircleRadius}" fill="${fillColor}" stroke="#CCCCCC" stroke-width="1" filter="url(#shadow)"/>
            <text x="0" y="0" font-size="${
              mainCircleRadius / 2
            }" font-family="'Arial'" font-weight="bold" text-anchor="middle" fill="#000000CC" dy="-.25em">${tableNumber}</text>
            <text x="0" y="${mainCircleRadius / 4}" font-size="${
    mainCircleRadius / 3
  }" font-family="'Arial'" text-anchor="middle" fill="#00000099" dy=".5em">${numberOfSeats} seats</text>`

  // Close the SVG string
  svgString += `</g></svg>`

  // Encode the SVG string into a Data URI and return it
  return {
    html: svgString,
    iconSize: [svgWidth, svgHeight] as L.PointExpression,
    iconAncor: [
      (svgWidth * svgScaleFactor) / 2,
      (svgHeight * svgScaleFactor) / 2,
    ] as L.PointExpression,
  }
}

export const DraggableTable: FunctionComponent<MapShapeProps> = shape => {
  const { x, y, label, onDrag } = shape

  const markerRef = useRef<L.Marker>(null)

  const [isHovered, setIsHovered] = useState(false)

  const position = {
    lng: x,
    lat: y,
  }

  const svgProps = useMemo(() => {
    return generateSvg({
      tableNumber: `${label}`,
      numberOfSeats: shape.maximumAllowedToBook ?? 0,
      svgScaleFactor: 0.5,
      rotation: 0,
      hover: isHovered || shape.isSelected,
    })
  }, [label, shape.maximumAllowedToBook, isHovered, shape.isSelected])

  const icon = useMemo(() => {
    return L.divIcon({
      className: 'svg-icon',
      ...svgProps,
    })
  }, [svgProps])

  const eventHandlers = useMemo(
    () => ({
      dragstart() {
        const marker = markerRef.current
        if (marker != null) {
          const element = marker.getElement()
          if (element) {
            element.style.zIndex = '1000'
          }
        }
      },
      dragend() {
        const marker = markerRef.current
        if (marker != null) {
          const element = marker.getElement()
          if (element) {
            element.style.zIndex = 'auto'
          }
          const latLng = marker.getLatLng()

          onDrag?.({
            ...shape,
            x: getAuthorizedValue(latLng.lng, 0, shape.mapSize.width),
            y: getAuthorizedValue(latLng.lat, 0, shape.mapSize.height),
          })
        }
      },
      mouseover() {
        setIsHovered(true)
      },
      mouseout() {
        setIsHovered(false)
      },
      click() {
        shape.onClick?.(shape)
      },
    }),
    [onDrag, shape],
  )

  return (
    <Marker
      draggable={shape.draggable}
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
      icon={icon}
    />
  )
}
